import {
    type ChangeEvent,
    type FormEvent,
    type KeyboardEvent,
    useCallback,
    useRef,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import { DEAL_NOTE_MAX_LENGTH, type TCreateDealCommentPayload } from '@crm/entities/deal';
import { errorHandleMiddleware, FileItemRaw } from '@entities/attachment';
import { COMMENT_ATTACHMENT_EXTENSIONS } from '@entities/comment';
import { Icon, TextArea, UiButton } from '@shared/ui';

import cls from './DealNotesFooter.module.css';

const MAX_FILES_COUNT = 10;
const MAX_FILE_SIZE_MB = 5;
const MAX_TEXTAREA_ROWS = 5;

interface DealNotesFooterProps {
    isPending?: boolean;
    onSubmit: (payload: TCreateDealCommentPayload) => Promise<unknown>;
}

export const DealNotesFooter = ({ isPending = false, onSubmit }: DealNotesFooterProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [text, setText] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const canSubmit = text.trim().length > 0 || files.length > 0;

    const handleOpenFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleAddFiles = useCallback((selectedFiles: File[]) => {
        setFiles((currentFiles) => [...currentFiles, ...selectedFiles]);
    }, []);

    const handleUploadFiles = errorHandleMiddleware({
        t,
        onAddFiles: handleAddFiles,
        alreadyAttachedLength: files.length,
        maxAttachmentSizeInMb: MAX_FILE_SIZE_MB,
        maxFiles: MAX_FILES_COUNT,
        validateExtensions: COMMENT_ATTACHMENT_EXTENSIONS,
        multiple: true,
    });

    const handleFileChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const selectedFiles = Array.from(event.target.files ?? []);

            if (selectedFiles.length > 0) {
                handleUploadFiles(selectedFiles);
            }

            event.target.value = '';
        },
        [handleUploadFiles]
    );

    const handleCancelFile = useCallback((cancelledFile: File) => {
        setFiles((currentFiles) => currentFiles.filter((file) => file !== cancelledFile));
    }, []);

    const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (
            event.key !== 'Enter' ||
            event.shiftKey ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey
        ) {
            return;
        }

        event.preventDefault();
        event.currentTarget.form?.requestSubmit();
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!canSubmit || isPending) {
            return;
        }

        await onSubmit({ text, files });
        setText('');
        setFiles([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <form className={cls.FooterContainer} onSubmit={handleSubmit}>
            {files.length > 0 && (
                <ul className={cls.AttachmentsContainer}>
                    {files.map((file, index) => (
                        <FileItemRaw
                            key={`${file.name}-${file.lastModified}-${file.size}-${index}`}
                            file={file}
                            isLoading={false}
                            fileItemClassName={cls.FileItemRaw}
                            onCancel={handleCancelFile}
                        />
                    ))}
                </ul>
            )}
            <div className={cls.FooterRow}>
                <input
                    ref={fileInputRef}
                    type="file"
                    className={cls.hidden}
                    multiple
                    onChange={handleFileChange}
                />
                <UiButton
                    variant="icon"
                    className={cls.FileInputBtn}
                    aria-label={t('crm.dealDetailsContent.notesBlock.footer.attachFileAriaLabel')}
                    disabled={isPending}
                    startIcon={<Icon iconName="paperClip" width={11} height={18} />}
                    onClick={handleOpenFilePicker}
                />
                <div className={cls.FooterInputContainer}>
                    <TextArea
                        inputName="deal-note-text"
                        classes={cls.FooterTextarea}
                        textareaClassName={cls.FooterInput}
                        maxLength={DEAL_NOTE_MAX_LENGTH}
                        value={text}
                        isDisabled={isPending}
                        minRows={1}
                        maxRows={MAX_TEXTAREA_ROWS}
                        autoResize
                        onChange={setText}
                        onKeyDown={handleTextareaKeyDown}
                    />
                </div>
                <UiButton
                    variant="icon"
                    type="submit"
                    className={cls.SendBtn}
                    aria-label={t('crm.dealDetailsContent.notesBlock.footer.sendNoteAriaLabel')}
                    disabled={!canSubmit || isPending}
                    startIcon={
                        <Icon
                            iconName="sendNotesDeal"
                            fill={!(canSubmit || isPending) ? '#818998' : undefined}
                        />
                    }
                />
            </div>
        </form>
    );
};
